import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, CheckCircle2, Mail, Phone, MapPin, Linkedin } from "lucide-react";
import { industryOptions } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", company: "", email: "", phone: "", industry: "", challenge: "", message: "" };

const inputClass =
  "w-full bg-[#0a0a0a] border border-[#313131] rounded-lg px-4 py-3 text-sm text-white placeholder:text-[#7c7c7c] focus:outline-none focus:border-[#15b351] focus:ring-1 focus:ring-[#15b351] transition-colors";

const labelClass = "block font-mono text-[11px] uppercase tracking-[0.085em] text-[#7c7c7c] mb-2";

export const Contact = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please provide your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Request received. We'll be in touch shortly.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-[#0a0a0a] border-t border-[#1e1e1e]">
      <div className="mx-auto max-w-[1200px] px-6 lg:px-8 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20">
          <div className="lg:sticky lg:top-28 lg:self-start">
            <p className="eyebrow">Contact</p>
            <h2 className="mt-5 text-4xl lg:text-5xl font-semibold tracking-[-0.03em] leading-[1.05] text-white">
              Let's build better systems
            </h2>
            <p className="mt-6 text-base text-[#a7a7a7] leading-relaxed max-w-md">
              Whether you're looking to automate operations, implement AI, improve visibility, or build custom
              software, we're ready to help.
            </p>
            <div className="mt-10 space-y-4">
              {["Free discovery consultation to scope your opportunity.", "A clear roadmap before any build begins.", "End-to-end delivery and long-term partnership."].map((t) => (
                <div key={t} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-[#15b351] mt-0.5 shrink-0" />
                  <p className="text-sm text-[#a7a7a7]">{t}</p>
                </div>
              ))}
            </div>

            <div className="mt-10 pt-8 border-t border-[#1e1e1e] space-y-4">
              <a href="mailto:askus@graphikpro.com" data-testid="contact-email-link" className="flex items-center gap-3 text-sm text-[#a7a7a7] hover:text-white transition-colors group">
                <span className="flex h-9 w-9 items-center justify-center rounded-md border border-[#1e1e1e] bg-[#141414] text-[#15b351] group-hover:border-[#15b351]/40 transition-colors"><Mail className="h-4 w-4" /></span>
                askus@graphikpro.com
              </a>
              <a href="tel:+919342055964" data-testid="contact-phone-link" className="flex items-center gap-3 text-sm text-[#a7a7a7] hover:text-white transition-colors group">
                <span className="flex h-9 w-9 items-center justify-center rounded-md border border-[#1e1e1e] bg-[#141414] text-[#15b351] group-hover:border-[#15b351]/40 transition-colors"><Phone className="h-4 w-4" /></span>
                +91 93420 55964
              </a>
              <a href="https://www.linkedin.com/in/madhiee/" target="_blank" rel="noopener noreferrer" data-testid="contact-linkedin-link" className="flex items-center gap-3 text-sm text-[#a7a7a7] hover:text-white transition-colors group">
                <span className="flex h-9 w-9 items-center justify-center rounded-md border border-[#1e1e1e] bg-[#141414] text-[#15b351] group-hover:border-[#15b351]/40 transition-colors"><Linkedin className="h-4 w-4" /></span>
                LinkedIn
              </a>
              <div className="flex items-start gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-md border border-[#1e1e1e] bg-[#141414] text-[#15b351] shrink-0"><MapPin className="h-4 w-4" /></span>
                <address className="not-italic text-sm text-[#a7a7a7] leading-relaxed">
                  Graphikpro Private Limited, 30, Subrayan Street, Tagore Nagar, Lawspet, Puducherry - 605008
                </address>
              </div>
            </div>
          </div>
            <div data-testid="contact-success" className="rounded-lg border border-[#1e1e1e] bg-[#141414] p-10 flex flex-col items-start justify-center">
              <CheckCircle2 className="h-12 w-12 text-[#15b351]" />
              <h3 className="mt-5 text-2xl font-semibold tracking-tight text-white">Thank you</h3>
              <p className="mt-3 text-base text-[#a7a7a7] max-w-sm">
                Your consultation request has been received. A member of our team will reach out to you shortly.
              </p>
              <button
                data-testid="contact-reset-btn"
                onClick={() => setDone(false)}
                className="mt-8 text-sm font-medium text-[#15b351] hover:underline"
              >
                Send another request
              </button>
            </div>
          ) : (
            <form data-testid="contact-form" onSubmit={submit} className="rounded-lg border border-[#1e1e1e] bg-[#141414] p-8 lg:p-10">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className={labelClass}>Name *</label>
                  <input data-testid="contact-name" value={form.name} onChange={set("name")} className={inputClass} placeholder="Jane Doe" required />
                </div>
                <div>
                  <label className={labelClass}>Company</label>
                  <input data-testid="contact-company" value={form.company} onChange={set("company")} className={inputClass} placeholder="Acme Inc." />
                </div>
                <div>
                  <label className={labelClass}>Email *</label>
                  <input data-testid="contact-email" type="email" value={form.email} onChange={set("email")} className={inputClass} placeholder="jane@company.com" required />
                </div>
                <div>
                  <label className={labelClass}>Phone</label>
                  <input data-testid="contact-phone" value={form.phone} onChange={set("phone")} className={inputClass} placeholder="+1 (555) 000-0000" />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelClass}>Industry</label>
                  <select data-testid="contact-industry" value={form.industry} onChange={set("industry")} className={inputClass}>
                    <option value="">Select an industry</option>
                    {industryOptions.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className={labelClass}>Current Challenge</label>
                  <input data-testid="contact-challenge" value={form.challenge} onChange={set("challenge")} className={inputClass} placeholder="What's slowing your operations down?" />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelClass}>Message</label>
                  <textarea data-testid="contact-message" value={form.message} onChange={set("message")} rows={4} className={inputClass} placeholder="Tell us more about your goals..." />
                </div>
              </div>

              <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={loading}
                className="mt-7 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-white px-6 py-3.5 text-sm font-medium text-[#0a0a0a] hover:bg-[#15b351] hover:text-white transition-colors disabled:opacity-60"
              >
                {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending...</> : <>Book a Free Consultation <ArrowUpRight className="h-4 w-4" /></>}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
